import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Users, Calendar, Award, TrendingUp, FileText, Clock } from 'lucide-react';

const ZohoPeople = () => {
  const features = [
    {
      icon: <Users className="w-6 h-6 text-blue-700" />,
      title: 'Personeelsbeheer',
      description: 'Centraal beheer van alle medewerker informatie en documenten.',
    },
    {
      icon: <Calendar className="w-6 h-6 text-blue-700" />,
      title: 'Verlofbeheer',
      description: 'Eenvoudig aanvragen en goedkeuren van verlof en verzuim.',
    },
    {
      icon: <Clock className="w-6 h-6 text-blue-700" />,
      title: 'Tijdregistratie',
      description: 'Volg werkuren en presentie met tijdregistratie.',
    },
    {
      icon: <Award className="w-6 h-6 text-blue-700" />,
      title: 'Performance Management',
      description: 'Voer functioneringsgesprekken en beoordelingen uit.',
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-700" />,
      title: 'Onboarding',
      description: 'Gestructureerde onboarding van nieuwe medewerkers.',
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-700" />,
      title: 'HR Analytics',
      description: 'Inzicht in personeelssamenstelling en trends.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                HR Management Systeem
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho People: Moderner HR Management Voor Groeiende Bedrijven
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Stroomlijn uw HR processen met Zoho People. Van personeelsbeheer tot performance management - 
                alles in één overzichtelijk systeem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1700887944225-f148dd124305?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMGludGVyZmFjZXxlbnwwfHx8fDE3NzM4MTc2MjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Zoho People Dashboard"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho People Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho People?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho People is een modern HR management systeem (HRMS) dat alle aspecten van personeelsbeheer 
                centraliseert en automatiseert. Van het aannemen van nieuwe medewerkers tot het beheren van verlof 
                en performance reviews - Zoho People helpt HR teams om efficiënter te werken.
              </p>
              <p>
                Als Zoho Partner helpt Guijs Consultancy u met de implementatie van Zoho People volgens Nederlandse 
                HR wetgeving en arbeidsrecht. Wij zorgen voor de juiste configuratie van verlofsoorten, contractvormen 
                en andere Nederlandse specifieke HR elementen.
              </p>
              <p>
                Zoho People groeit mee met uw organisatie - van een klein team tot honderden medewerkers. Het systeem 
                biedt de flexibiliteit om mee te schalen en ondersteunt zowel kantoor- als remote medewerkers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho People
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alles wat u nodig heeft voor modern HR management.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho People
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho People helpt HR teams om strategischer te werken door administratieve taken te automatiseren.
              </p>

              <ul className="space-y-4">
                {[
                  'Bespaar uren op HR administratie en papierwerk',
                  'Verbeter medewerker tevredenheid met self-service',
                  'Krijg inzicht in personeelstrends en HR metrics',
                  'Voldoe aan Nederlandse arbeidsrecht en privacy wet',
                  'Onboard nieuwe medewerkers sneller en beter',
                  'Centraliseer alle HR data in één systeem',
                ].map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <img
                src="https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzczNzQ2NTMwfDA&ixlib=rb-4.1.0&q=85"
                alt="HR Management"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Zoho People?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij zorgen voor een Zoho People implementatie die aansluit op Nederlandse HR praktijken.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Nederlandse Configuratie</h3>
                  <p className="text-gray-600">
                    Wij configureren Zoho People volgens Nederlandse arbeidsrecht en HR conventies.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Privacy & AVG</h3>
                  <p className="text-gray-600">
                    Volledige naleving van AVG wetgeving voor veilige opslag van personeelsdata.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Salarisadministratie Koppeling</h3>
                  <p className="text-gray-600">
                    Integratie met Nederlandse salarisadministratie systemen.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Change Management</h3>
                  <p className="text-gray-600">
                    Begeleiding van zowel HR als medewerkers voor succesvolle adoptie.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar voor Modern HR Management?"
        subtitle="Ontdek hoe Zoho People uw HR processen kan transformeren."
      />
    </div>
  );
};

export default ZohoPeople;
